import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Validators } from '@angular/forms';
import { DefaultValuesService } from '../../../../services/defaultvalues.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AssessmentChangelogComponent } from './assessment-changelog/assessment-changelog.component';
import { Utils } from '../../../../utils/utils';
import { Router } from '@angular/router';
import { DialogService } from '../../../../services/dialog.service';
import { TableColumn } from '../../shared-components/data-table/data-table.component';
import { AddEditDialogComponent, FormFieldConfig } from '../../shared-components/add-edit-dialog/add-edit-dialog.component';
import { OshAttachmentsDialogComponent, OshAttachmentsDialogData } from '../attachments-dialog/attachments-dialog.component';
import { ConfirmationDialogComponent } from '../../../../components/dialogbox/dialog-confirmation.component';
import { OshAttachmentEntityType } from '../../../../interfaces/osh/oshattachment';

@Component({
    selector: 'osh-assessment',
    templateUrl: './oshassessment.component.html',
    styleUrls: ['./oshassessment.component.less',
        '../../../../style/new-generic-styles.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OshAssessmentComponent implements OnInit, OnDestroy {

    private readonly _destroying$ = new Subject<void>();
    
    currentWaveId: number = -1;
    protocols: OshProtocol[] = [];
    protocolFactors: ProtocolFactorAssessment[] = [];
    selectedProtocolId: number = -1;
    changedFactors: Set<number> = new Set<number>();
    
    isLoading = false;
    isLoadingFactors = false;
    isEditModeEnabled = false;
    isExcelModeEnabled = false;
    isOpenTextAreaOverlay = false;
    
    displayedColumns: string[] = ['factorCode', 'factorDescription', 'stage', 'priority', 'observations', 'recommendations', 'history', 'attachments'];
    
    // Action Items properties
    actionItems: ActionItem[] = [];
    tableData: any[] = [];
    tableColumns: TableColumn[] = [];
    factors: any[] = [];
    allProtocolFactors: ProtocolFactorAssessment[] = []; // Store all factors from assessment endpoint
    isLoadingActionItems: boolean = false;
    // Priority factors (from the assessment) that don't yet have a linked action item
    unlinkedPriorityFactors: { factorId: number; factorCode: string; factorDescription: string }[] = [];
    
    stages: StageOption[] = [
        { id: null, label: '-' },
        { id: 1, label: 'Foundation' },
        { id: 2, label: 'Demonstrating' },
        { id: 3, label: 'Performing' },
        { id: 4, label: 'Excelling' }
    ];

    oshUserSuggestions: { label: string; value: string }[] = [];

    constructor(
        private readonly http: HttpClient,
        @Inject('BASE_URL') private readonly baseUrl: string,
        public defaults: DefaultValuesService,
        private readonly toastrService: ToastrService,
        private readonly dialog: MatDialog,
        private readonly dialogService: DialogService,
        private readonly router: Router
    ) {}

    private loadOshUsers(): void {
        this.http.get<{ displayName: string; mail: string }[]>(`${this.baseUrl}api/osh/users`).subscribe({
            next: (users) => {
                this.oshUserSuggestions = (users || [])
                    .filter(u => !!u.mail)
                    .map(u => ({ label: u.displayName || u.mail, value: u.mail }));
            },
            error: (error) => {
                console.error('Error loading SSX users:', error);
                this.toastrService.error('Failed to load the SSX user list. Owner and recipient selection will be unavailable.');
            }
        });
    }

    ngOnInit(): void {
        this.initializeActionItemsColumns();
        this.loadOshUsers();

        this.defaults.oshWave$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this._destroying$)
            )
            .subscribe((waveId: number | undefined) => {
                if (waveId && waveId > 0) {
                    this.currentWaveId = waveId;
                    this.loadProtocolsForWave(waveId);
                } else {
                    this.currentWaveId = -1;
                    this.protocols = [];
                }
            });

        this.defaults.managementIdMessageChanges$
            .pipe(takeUntil(this._destroying$))
            .subscribe(() => {
                if (this.currentWaveId > 0) {
                    this.loadProtocolsForWave(this.currentWaveId);
                }
            });   

        this.defaults.languageMessageChanges$
            .pipe(takeUntil(this._destroying$))
            .subscribe(() => {
                if (this.currentWaveId > 0 && this.selectedProtocolId > 0) {
                    this.loadProtocolFactors(this.selectedProtocolId);
                }
            });

        if (this.defaults.oshWaveId && this.defaults.oshWaveId > 0) {
            this.currentWaveId = this.defaults.oshWaveId;
            this.loadProtocolsForWave(this.defaults.oshWaveId);
        }
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    loadProtocolsForWave(waveId: number) {
        this.isLoading = true;
        this.selectedProtocolId = -1;
        this.isEditModeEnabled = false;
        this.isExcelModeEnabled = false;
        this.changedFactors.clear();
        const protocolsUrl = `${this.baseUrl}api/osh/waves/${waveId}/protocols`;
        
        this.http.get<OshWaveProtocolConfig[]>(protocolsUrl).subscribe({
            next: (waveProtocolConfigs) => {
                this.loadProtocols(waveProtocolConfigs);
            },
            error: (error) => {
                this.isLoading = false;
                this.protocols = [];
                this.toastrService.error('Error loading protocols for wave');
            }
        });
    }

    loadProtocols(waveProtocolConfigs: OshWaveProtocolConfig[]) {
        if (!waveProtocolConfigs || waveProtocolConfigs.length === 0) {
            this.protocols = [];
            this.isLoading = false;
            this.toastrService.info('No protocols assigned to this wave');
            return;
        }

        this.protocols = waveProtocolConfigs
            .filter(config => config.isActive)
            .map(config => ({
                id: config.protocolId,
                code: config.protocolCode,
                name: config.protocolName,
                isActive: config.isActive
            }));
        
        this.isLoading = false;

        // Load factors for the first protocol by default
        if (this.protocols.length > 0) {
            this.onProtocolTabChange(0);
        }
    }

    onProtocolTabChange(tabIndex: number): void {
        // Check if it's the Action Items tab (last tab)
        if (tabIndex === this.protocols.length) {
            this.loadActionItems();
            return;
        }
        
        if (tabIndex < 0 || tabIndex >= this.protocols.length) {
            return;
        }

        const protocolId = this.protocols[tabIndex].id;
        
        if (this.selectedProtocolId === protocolId) {
            return;
        }

        this.selectedProtocolId = protocolId;
        this.defaults.updateComponentTab(this.protocols[tabIndex].name);
        this.loadProtocolFactors(protocolId);
    }

    loadProtocolFactors(protocolId: number): void {
        if (!this.defaults.managementid || this.currentWaveId <= 0) {
            this.protocolFactors = [];
            return;
        }

        this.isLoadingFactors = true;
        const assessmentUrl = `${this.baseUrl}api/osh/assessment/${this.defaults.managementid}/${this.currentWaveId}`;

        this.http.get<ProtocolFactorAssessment[]>(assessmentUrl).subscribe({
            next: (allFactors) => {
                // Store all factors for use in Action Items
                this.allProtocolFactors = allFactors;
                
                // Sort all factors by protocolId then by factorId
                const sortedFactors = [...allFactors].sort((a, b) => {
                    if (a.protocolId !== b.protocolId) {
                        return a.protocolId - b.protocolId;
                    }
                    return a.factorId - b.factorId;
                });
                
                // Populate factors dropdown for Action Items modal
                this.factors = sortedFactors.map(f => ({
                    value: f.factorId,
                    label: `${f.factorCode} - ${f.factorDescription || ''}`,
                    priority: !!f.isPriority
                }));

                // Remove duplicates (same factor can appear in multiple protocols)
                const uniqueFactors = new Map();
                this.factors.forEach(f => {
                    if (!uniqueFactors.has(f.value)) {
                        uniqueFactors.set(f.value, f);
                    }
                });
                this.factors = Array.from(uniqueFactors.values());
                // Show priority factors first so they stand out during action planning
                this.factors.sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));
                // Filter factors for the selected protocol
                this.protocolFactors = allFactors.filter(f => f.protocolId === protocolId);
                this.isLoadingFactors = false;
            },
            error: (error) => {
                this.isLoadingFactors = false;
                this.protocolFactors = [];
                this.allProtocolFactors = [];
                this.toastrService.error('Error loading protocol factors');
                console.error('Error loading protocol factors:', error);
            }
        });
    }

    toggleEditMode(): void {
        if (this.isEditModeEnabled) {
            // Undo mode - discard changes and reload data
            this.isEditModeEnabled = false;
            if (this.changedFactors.size > 0) {
                // Reload the factors to discard changes
                this.loadProtocolFactors(this.selectedProtocolId);
                this.changedFactors.clear();
            }
        } else {
            // Entering edit mode
            this.changedFactors.clear();
            this.isEditModeEnabled = true;
        }
    }

    onClickExcel(): void {
        this.isExcelModeEnabled = true;
        this.isEditModeEnabled = false;
    }

    onClickExitExcel(): void {
        this.isExcelModeEnabled = false;
    }

    onStageChange(factor: ProtocolFactorAssessment): void {
        this.changedFactors.add(factor.factorId);
    }

    onPriorityChange(factor: ProtocolFactorAssessment): void {
        this.changedFactors.add(factor.factorId);
    }

    onTextChange(factor: ProtocolFactorAssessment): void {
        // Mark factor as changed
        this.changedFactors.add(factor.factorId);
    }

    onTextFieldChange(comment: string, textAreaId: string, factor: ProtocolFactorAssessment) {
        if (factor) {
            const type = textAreaId.split('_')[0];
            switch (type) {
                case 'Observations':
                    factor.observations = comment;
                    break;
                case 'Recommendations':
                    factor.recommendations = comment;
                    break;
            }
            this.changedFactors.add(factor.factorId);
        }
    }

    saveAssessments(): void {
        if (this.changedFactors.size === 0) {
            this.isEditModeEnabled = false;
            this.toastrService.info('No changes to save');
            return;
        }

        // Prepare the assessments to save
        const assessmentsToSave: SaveAssessmentRequest[] = [];

        this.changedFactors.forEach(factorId => {
            const factor = this.protocolFactors.find(f => f.factorId === factorId);
            if (factor) {
                assessmentsToSave.push({
                    mgmtId: factor.branch,
                    waveId: factor.waveId,
                    factorId: factor.factorId,
                    factorStageAssessment: factor.factorStageAssessment,
                    isPriority: factor.isPriority,
                    observations: factor.observations,
                    recommendations: factor.recommendations
                });
            }
        });

        if (assessmentsToSave.length === 0) {
            this.isEditModeEnabled = false;
            this.toastrService.warning('No valid assessments to save');
            return;
        }

        const saveUrl = `${this.baseUrl}api/osh/assessment/save`;

        this.http.post(saveUrl, assessmentsToSave).subscribe({
            next: (response: any) => {
                this.toastrService.success(response?.message || `Successfully saved ${assessmentsToSave.length} assessment(s)`);
                this.changedFactors.clear();
                this.isEditModeEnabled = false;
                this.loadProtocolFactors(this.selectedProtocolId);
            },
            error: () => {
                this.toastrService.error('Error saving assessments');
            }
        });
    }

    getStageLabel(stageId: number | null): string {
        if (stageId === null || stageId === undefined) {
            return '-';
        }
        const stage = this.stages.find(s => s.id === stageId);
        return stage ? stage.label : '-';
    }

    getStageDescription(factor: ProtocolFactorAssessment): string {
        const stageId = factor.factorStageAssessment;
        if (!stageId) return '';
        
        switch (stageId) {
            case 1: return factor.stage1 || '';
            case 2: return factor.stage2 || '';
            case 3: return factor.stage3 || '';
            case 4: return factor.stage4 || '';
            default: return '';
        }
    }

    viewAssessmentHistory(factor: ProtocolFactorAssessment) {
        if (!factor.assessmentId) {
            this.toastrService.warning('No assessment history available for this factor.');
            return;
        }

        this.dialog.open(AssessmentChangelogComponent, {
            disableClose: false,
            width: '1100px',
            height: '65vh',
            data: {
                assessmentId: factor.assessmentId,
                element: {
                    factorCode: factor.factorCode,
                    factorDescription: factor.factorDescription,
                    protocolName: factor.protocolName,
                    protocolCode: factor.protocolCode
                }
            }
        });
    }

    onClickDownloadAssessments() {
        if (!this.defaults.managementid || this.currentWaveId <= 0) {
            this.toastrService.warning('No wave or branch selected.');
            return;
        }

        Utils.httpDownloadOshAssessments(
            this.http,
            this.baseUrl,
            `OshAssessments_${this.defaults.managementid}_${this.currentWaveId}`,
            this.defaults.managementid,
            this.currentWaveId
        );
    }

    onClickUploadAssessments() {
        if (!this.defaults.managementid || this.currentWaveId <= 0) {
            this.toastrService.warning('No wave or branch selected.');
            return;
        }

        this.router.navigate([`file/${Utils.getFileUploadOshAssessments()}-${this.currentWaveId}-${this.defaults.managementid}`]);
    }

    // ==================== ACTION ITEMS METHODS ====================
    
    private initializeActionItemsColumns(): void {
        this.tableColumns = [
            {
                key: 'factor',
                header: 'Factor',
                width: '8%',
                align: 'left',
                editable: false,
                formatter: (value: string, row: any) => row?.isPriority ? `★ ${value || '-'}` : (value || '-'),
                cellClass: (_value: any, row: any) => row?.isPriority ? 'priority-cell' : null
            },
            {
                key: 'title',
                header: 'Title',
                width: '10%',
                align: 'left',
                editable: false
            },
            {
                key: 'description',
                header: 'Description',
                width: '29%',
                align: 'left',
                editable: false
            },
            {
                key: 'dueDate',
                header: 'Due Date',
                width: '5%',
                align: 'center',
                editable: false,
                formatter: (value: Date) => value ? this.formatDate(value) : '-'
            },
            {
                key: 'owner',
                header: 'Owner',
                width: '10%',
                align: 'center',
                editable: false
            },
            {
                key: 'status',
                header: 'Status',
                width: '5%',
                align: 'center',
                editable: false,
                formatter: (value) => value ? 'Open' : 'Closed'
            },
            {
                key: 'progress',
                header: 'Progress (%)',
                width: '8%',
                align: 'center',
                editable: false
            },
            {
                key: 'comment',
                header: 'Closing comment',
                width: '15%',
                align: 'left',
                editable: false
            },
            {
                key: 'actions',
                header: 'Actions',
                width: '10%',
                align: 'center',
                editable: false
            }
        ];
    }

    loadActionItems(): void {
        if (!this.defaults.managementid || this.currentWaveId <= 0) {
            this.actionItems = [];
            this.tableData = [];
            return;
        }

        this.isLoadingActionItems = true;
        const url = `${this.baseUrl}api/osh/actionitems?mgmtId=${this.defaults.managementid}&waveId=${this.currentWaveId}`;

        this.http.get<ActionItem[]>(url).subscribe({
            next: (data) => {
                this.actionItems = data;
                this.prepareActionItemsTableData();
                this.isLoadingActionItems = false;
            },
            error: (error) => {
                console.error('Error loading action items:', error);
                this.toastrService.error('Failed to load action items');
                this.actionItems = [];
                this.tableData = [];
                this.isLoadingActionItems = false;
            }
        });
    }

    prepareActionItemsTableData(): void {
        const priorityFactorIds = new Set(
            this.allProtocolFactors.filter(f => f.isPriority).map(f => f.factorId)
        );

        this.tableData = this.actionItems.map(item => ({
            id: item.id,
            factor: item.factor || '-',
            isPriority: priorityFactorIds.has(item.factorId),
            title: item.title,
            description: item.description,
            dueDate: item.dueDate,
            owner: item.owner,
            status: item.isActive,
            progress: item.progress ?? null,
            actions: item.id,
            comment: item.comment || '-',
            alerts: item.alerts || []
        }));

        this.computeUnlinkedPriorityFactors();
    }

    // A priority finding must be followed up: every priority factor needs at least one linked action.
    private computeUnlinkedPriorityFactors(): void {
        const linkedFactorIds = new Set(this.actionItems.map(a => a.factorId));
        const seen = new Set<number>();
        this.unlinkedPriorityFactors = [];

        this.allProtocolFactors.forEach(f => {
            if (f.isPriority && !seen.has(f.factorId)) {
                seen.add(f.factorId);
                if (!linkedFactorIds.has(f.factorId)) {
                    this.unlinkedPriorityFactors.push({
                        factorId: f.factorId,
                        factorCode: f.factorCode,
                        factorDescription: f.factorDescription
                    });
                }
            }
        });
    }

    getCellValue(row: any, column: TableColumn): string {
        const value = row[column.key];
        if (column.formatter) {
            return column.formatter(value, row);
        }
        return value || '-';
    }
    
    onAddActionItem(): void {
        // If priority findings still lack an action, force the user to link one of those first.
        const hasUnlinkedPriority = this.unlinkedPriorityFactors.length > 0;
        const factorOptions = hasUnlinkedPriority
            ? this.factors.filter(f => this.unlinkedPriorityFactors.some(u => u.factorId === f.value))
            : this.factors;

        const fields: FormFieldConfig[] = [
            {
                key: 'factorId',
                label: 'Factor',
                type: 'select',
                required: true,
                options: factorOptions,
                placeholder: 'Select a factor',
                legend: hasUnlinkedPriority
                    ? 'Priority items require an action first — only unlinked priority items are selectable until each has at least one action.'
                    : undefined
            },
            {
                key: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                maxLength: 200,
                placeholder: 'Enter action item title'
            },
            {
                key: 'description',
                label: 'Description',
                type: 'textarea',
                required: true,
                maxLength: 2000,
                rows: 6,
                placeholder: 'Enter detailed description'
            },
            {
                key: 'dueDate',
                label: 'Due Date',
                type: 'date',
                required: true,
                colSpan: 6
            },
            {
                key: 'owner',
                label: 'Owner',
                type: 'autocomplete',
                required: true,
                maxLength: 100,
                placeholder: 'Type a name or email',
                validators: [Validators.email],
                requiredMessage: 'Owner email is required',
                suggestions: this.oshUserSuggestions,
                restrictToSuggestions: true,
                colSpan: 6
            },
            {
                key: 'isActive',
                label: 'Status',
                type: 'ngselect',
                required: true,
                options: [
                    { value: true, label: 'Open' },
                    { value: false, label: 'Closed' }
                ],
                placeholder: 'Select status',
                colSpan: 6,
                defaultValue: true
            },
            {
                key: 'comment',
                label: 'Closing comment',
                type: 'textarea',
                required: false,
                maxLength: 2000,
                rows: 4,
                placeholder: 'Enter comment (required when status is Closed)',
                dependsOn: 'isActive',
                showWhen: (value: any) => value === false,
                colSpan: 12
            }
        ];

        const dialogRef = this.dialog.open(AddEditDialogComponent, {
            width: '600px',
            panelClass: 'dialogNgselect',
            data: {
                title: 'Create New Action Item',
                fields: fields,
                apiEndpoint: 'api/osh/actionitems',
                isEditMode: false,
                submitButtonText: 'Create',
                successMessage: 'Action item created successfully!',
                mapDataToApi: (formData: any, additionalData: any) => {
                    return {
                        FactorId: formData.factorId,
                        Title: formData.title,
                        Description: formData.description,
                        DueDate: formData.dueDate,
                        Owner: formData.owner,
                        IsActive: formData.isActive,
                        Progress: this.normalizeProgress(formData.progress),
                        Comment: formData.comment || null,
                        MgmtId: this.defaults.managementid,
                        WaveId: this.currentWaveId
                    };
                }
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadActionItems();
            }
        });
    }

    onEditActionItem(id: number): void {
        const actionItem = this.actionItems.find(item => item.id === id);
        if (!actionItem) {
            return;
        }

        console.log('Action Item to edit:', actionItem);

        const fields: FormFieldConfig[] = [
            {
                key: 'factorId',
                label: 'Factor',
                type: 'select',
                required: true,
                options: this.factors,
                placeholder: 'Select a factor'
            },
            {
                key: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                maxLength: 200,
                placeholder: 'Enter action item title'
            },
            {
                key: 'description',
                label: 'Description',
                type: 'textarea',
                required: true,
                maxLength: 2000,
                rows: 6,
                placeholder: 'Enter detailed description'
            },
            {
                key: 'dueDate',
                label: 'Due Date',
                type: 'date',
                required: true,
                colSpan: 6,
            },
            {
                key: 'owner',
                label: 'Owner',
                type: 'autocomplete',
                required: true,
                maxLength: 100,
                placeholder: 'Type a name or email',
                validators: [Validators.email],
                requiredMessage: 'Owner email is required',
                suggestions: this.oshUserSuggestions,
                restrictToSuggestions: true,
                colSpan: 6,
            },
            {
                key: 'isActive',
                label: 'Status',
                type: 'ngselect',
                required: true,
                options: [
                    { value: true, label: 'Open' },
                    { value: false, label: 'Closed' }
                ],
                placeholder: 'Select status',
                colSpan: 6
            },
            {
                key: 'progress',
                label: 'Progress (%)',
                type: 'number',
                required: false,
                min: '0',
                max: '100',
                placeholder: '0 - 100',
                colSpan: 6
            },
            {
                key: 'comment',
                label: 'Closing comment',
                type: 'textarea',
                required: false,
                maxLength: 2000,
                rows: 4,
                placeholder: 'Enter comment (required when status is Closed)',
                dependsOn: 'isActive',
                showWhen: (value: any) => value === false,
                colSpan: 12
            }
        ];

        const dialogRef = this.dialog.open(AddEditDialogComponent, {
            width: '600px',
            panelClass: 'dialogNgselect',
            data: {
                title: 'Edit Action Item',
                fields: fields,
                apiEndpoint: 'api/osh/actionitems',
                data: actionItem,
                isEditMode: true,
                submitButtonText: 'Update',
                successMessage: 'Action item updated successfully!',
                mapDataToApi: (formData: any, additionalData: any) => {
                    return {
                        Id: actionItem.id,
                        FactorId: formData.factorId,
                        Title: formData.title,
                        Description: formData.description,
                        DueDate: formData.dueDate,
                        Owner: formData.owner,
                        IsActive: formData.isActive,
                        Progress: this.normalizeProgress(formData.progress),
                        Comment: formData.comment || null
                    };
                }
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadActionItems();
            }
        });
    }

    onDeleteActionItem(id: number): void {
        const actionItem = this.actionItems.find(item => item.id === id);
        if (!actionItem) {
            return;
        }

        const confirmContext = {
            onYes: () => {
                this.dialogService.extraClasses = undefined;
                const url = `${this.baseUrl}api/osh/actionitems/${id}`;
                this.http.delete(url).subscribe({
                    next: () => {
                        this.toastrService.success('Action item deleted successfully');
                        this.loadActionItems();
                    },
                    error: (error) => {
                        console.error('Error deleting action item:', error);
                        this.toastrService.error('Failed to delete action item');
                    }
                });
            },
            onNo: () => {
                this.dialogService.extraClasses = undefined;
            }
        };

        this.dialogService.extraClasses = ['dialog-center-screen'];
        this.dialogService.showConfirmDialog(
            `Are you sure you want to delete the action item "${actionItem.title}"?`,
            'Delete Action Item',
            confirmContext
        );
    }

    onOpenActionItemAttachments(id: number): void {
        const actionItem = this.actionItems.find(item => item.id === id);
        let contextLabel: string | undefined;
        if (actionItem) {
            const factorSuffix = actionItem.factor ? ` (${actionItem.factor})` : '';
            contextLabel = `${actionItem.title}${factorSuffix}`;
        }
        this.openAttachmentsDialog(OshAttachmentEntityType.ActionItem, id, contextLabel);
    }

    onOpenAssessmentAttachments(factor: ProtocolFactorAssessment): void {
        if (!factor.assessmentId) {
            this.toastrService.warning('No assessment available for this factor. Please save an assessment first.');
            return;
        }
        this.openAttachmentsDialog(OshAttachmentEntityType.Assessment, factor.assessmentId, factor.factorCode);
    }

    private openAttachmentsDialog(entityType: OshAttachmentEntityType, entityId: number, contextLabel?: string): void {
        const dialogData: OshAttachmentsDialogData = {
            entityType,
            entityId,
            canEdit: this.defaults.isMersyAdminSafetySystemExcellence
                || this.defaults.isMersyAssessorSafetySystemExcellence
                || (entityType === OshAttachmentEntityType.ActionItem && this.defaults.isMersyReporterSafetySystemExcellence),
            contextLabel
        };

        this.dialog.open(OshAttachmentsDialogComponent, {
            width: '1000px',
            maxWidth: '95vw',
            height: '80vh',
            panelClass: 'attachments-dialog-panel',
            data: dialogData
        });
    }

    onConfigureAlert(id: number, defaultType: 'alert' | 'reminder' = 'alert'): void {
        const actionItem = this.actionItems.find(item => item.id === id);
        if (!actionItem) {
            return;
        }

        const existingAlert = actionItem.alerts?.find(alert => alert.type === defaultType);

        // Convert dueDate to ISO format for date input (YYYY-MM-DD)
        // Use local date parts to avoid UTC offset shifting the date across timezones
        const toLocalDateIso = (d: Date): string =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const dueDateIso = actionItem.dueDate 
            ? toLocalDateIso(new Date(actionItem.dueDate))
            : toLocalDateIso(new Date()); // Default to today if no due date

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        let minDate: string;
        if (dueDateIso >= toLocalDateIso(tomorrow)) {
            const dueDateObj = new Date(actionItem.dueDate);
            dueDateObj.setDate(dueDateObj.getDate() + 1);
            minDate = toLocalDateIso(dueDateObj);
        } else {
            minDate = toLocalDateIso(tomorrow);
        }

        const fields: FormFieldConfig[] = [
            {
                key: 'type',
                label: 'Type',
                type: 'select',
                options: [
                    { value: 'alert', label: 'Alert' },
                    { value: 'reminder', label: 'Reminder' }
                ],
                placeholder: 'Select type',
                disabled: true
            },
            {
                key: 'title',
                label: 'Title',
                type: 'text',
                required: true,
                maxLength: 200,
                placeholder: 'Enter alert/reminder title'
            },
            {
                key: 'description',
                label: 'Description',
                type: 'textarea',
                required: true,
                maxLength: 1000,
                rows: 3,
                placeholder: 'Enter description'
            },
            {
                key: 'dueDate',
                label: 'Due Date',
                type: 'date',
                required: false,
                colSpan: 6,
                disabled: true
            },
            {
                key: 'alertDate',
                label: defaultType === 'alert' ? 'Alert Date' : 'Reminder Date',
                type: 'date',
                required: true,
                colSpan: 6,
                legend: defaultType === 'alert' ? 'Day(s) after the overdue date (at 00:00 UTC)' : 'Day(s) until the due date (at 00:00 UTC)',
                min: defaultType === 'alert' ? minDate : undefined,
                max: defaultType === 'reminder' ? dueDateIso : undefined
            },
            {
                key: 'owner',
                label: 'Owner',
                type: 'text',
                required: false,
                colSpan: 6,
                disabled: true
            },
            {
                key: 'recipients',
                label: 'Recipients',
                type: 'email-chips',
                required: true,
                maxLength: 500,
                placeholder: 'Type a name or email',
                suggestions: this.oshUserSuggestions,
                restrictToSuggestions: true,
                colSpan: 12
            }
        ];

        const dialogRef = this.dialog.open(AddEditDialogComponent, {
            width: '600px',
            panelClass: 'dialogNgselect',
            data: {
                title: defaultType === 'alert' ? 'Configure Alert' : 'Configure Reminder',
                fields: fields,
                apiEndpoint: `api/osh/actionitems/${id}/alerts${existingAlert ? '/' + existingAlert.id : ''}`,
                data: existingAlert ? {
                    type: existingAlert.type || defaultType,
                    title: existingAlert.title,
                    description: existingAlert.description,
                    dueDate: actionItem.dueDate ,
                    alertDate: existingAlert.alertDate,
                    owner: actionItem.owner,
                    recipients: existingAlert.recipients
                } : {
                    type: defaultType,
                    dueDate: actionItem.dueDate,
                    owner: actionItem.owner
                },
                isEditMode: !!existingAlert,
                submitButtonText: existingAlert ? 'Update' : 'Create',
                successMessage: 'Configuration saved successfully',
                headerAction: existingAlert ? {
                    icon: 'delete',
                    tooltip: 'Delete alert',
                    color: '#f44336',
                    onClick: () => {
                        const deleteUrl = `${this.baseUrl}api/osh/actionitems/${id}/alerts/${existingAlert.id}`;
                        const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
                            data: {
                                title: 'Delete Alert',
                                message: 'Are you sure you want to delete this alert?'
                            }
                        });
                        confirmRef.afterClosed().subscribe((result: { confirmed: boolean }) => {
                            if (result?.confirmed) {
                                firstValueFrom(this.http.delete(deleteUrl)).then(() => {
                                    this.toastrService.success('Alert deleted successfully');
                                    this.loadActionItems();
                                    dialogRef.close();
                                }).catch((error) => {
                                    console.error('Error deleting alert:', error);
                                    this.toastrService.error('Failed to delete alert');
                                });
                            }
                        });
                    }
                } : undefined,
                onFieldChange: (fieldKey: string, value: any, fields: FormFieldConfig[], setTitle: (title: string) => void) => {
                    if (fieldKey === 'type') {
                        const alertDateField = fields.find(f => f.key === 'AlertDate');
                        if (alertDateField) {
                            if (value === 'alert') {
                                alertDateField.label = 'Alert Date';
                                alertDateField.legend = 'Day(s) after the overdue date (at 00:00 UTC)';
                                setTitle('Configure Alert');
                            } else if (value === 'reminder') {
                                alertDateField.label = 'Reminder Date';
                                alertDateField.legend = 'Day(s) until the due date (at 00:00 UTC)';
                                setTitle('Configure Reminder');
                            }
                        }
                    }
                },
                mapDataToApi: (formData: any) => {
                    if (existingAlert) {
                        return {
                            Id: existingAlert.id,
                            Title: formData.title,
                            Description: formData.description,
                            AlertDate: formData.alertDate,
                            Recipients: formData.recipients
                        };
                    } else {
                        return {
                            ActionItemId: id,
                            Type: formData.type,
                            Title: formData.title,
                            Description: formData.description,
                            AlertDate: formData.alertDate,
                            Recipients: formData.recipients
                        };
                    }
                }
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadActionItems();
            }
        });
    }

    hasAlert(row: any): boolean {
        if (!row.status) {
            return false;
        }
        
        return row.alerts?.some((alert: any) => alert.type === 'alert') || false;
    }

    hasReminder(row: any): boolean {
        if (!row.status) {
            return false;
        }
        
        return row.alerts?.some((alert: any) => alert.type === 'reminder') || false;
    }

    normalizeProgress(value: any): number | null {
        if (value === null || value === undefined || value === '') return null;
        const n = Math.round(Number(value));
        if (Number.isNaN(n)) return null;
        if (n < 0) return 0;
        if (n > 100) return 100;
        return n;
    }

    getProgressValue(row: any): number {
        const v = this.normalizeProgress(row?.progress);
        return v ?? 0;
    }

    hasProgress(row: any): boolean {
        return this.normalizeProgress(row?.progress) !== null;
    }

    disableReminder(row: any): boolean {
        if (!row.dueDate) return false;
        const d = new Date(row.dueDate);
        const dueDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return dueDateStr < todayStr;
    }

    private formatDate(date: Date): string {
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    }
}

export interface StageOption {
    id: number | null;
    label: string;
}

export interface SaveAssessmentRequest {
    mgmtId: string;
    waveId: number;
    factorId: number;
    factorStageAssessment: number | null;
    isPriority: boolean;
    observations: string;
    recommendations: string;
}

export interface OshProtocol {
    id: number;
    code: string;
    name: string;
    description?: string;
    isActive: boolean;
}

export interface OshWaveProtocolConfig {
    id: number;
    waveId: number;
    protocolId: number;
    protocolCode: string;
    protocolName: string;
    isActive: boolean;
}

export interface ProtocolFactorAssessment {
    branch: string;
    waveId: number;
    waveName: string;
    waveStartDate: Date;
    waveEndDate: Date;
    waveCreatedBy: string;
    waveChangedBy: string;
    protocolId: number;
    protocolCode: string;
    protocolName: string;
    protocolDescription: string;
    protocolCreatedBy: string;
    protocolChangedBy: string;
    factorId: number;
    factorCode: string;
    factorDescription: string;
    stage1: string;
    stage2: string;
    stage3: string;
    stage4: string;
    factorCreatedBy: string;
    factorChangedBy: string;
    assessmentId: number | null;
    factorStageAssessment: number | null;
    isPriority: boolean;
    observations: string;
    recommendations: string;
}

export interface ActionItem {
    id: number;
    factorId: number;
    factor: string;
    waveId: number;
    mgmtId: string;
    title: string;
    description: string;
    dueDate: Date;
    owner: string;
    isActive: boolean;
    progress?: number;
    comment?: string;
    createdDate?: Date;
    createdBy?: string;
    changedAtUtc?: Date;
    changedBy?: string;
    alerts?: ActionItemAlert[];
}

export interface ActionItemAlert {
    id: number;
    actionItemId: number;
    type: string;
    title: string;
    description: string;
    alertDate: Date;
    recipients: string;
}
